// packages/ui-kit/src/primitives/Radio.tsx
// C3F v18 — reference: design/system/components/forms/Radio.{jsx,prompt.md}
// Single choice in a vertical list. For 2-4 short inline options use
// SegmentedControl instead.
import { type InputHTMLAttributes, type ReactNode, forwardRef } from "react";

interface Props extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  /** Inline label rendered next to the circle. */
  label?: ReactNode;
  /** Secondary line under the label (muted caption). */
  description?: ReactNode;
}

/**
 * Shared radio — a system-drawn 22px circle over a visually-hidden native
 * input (accent ring + dot when selected). 44px touch target via the label
 * row; group radios with the native `name` attribute.
 */
export const Radio = forwardRef<HTMLInputElement, Props>(function Radio(
  { label, description, className = "", disabled, ...rest },
  ref,
) {
  const circle = (
    <span className="relative grid h-[22px] w-[22px] shrink-0 place-items-center">
      <input
        ref={ref}
        type="radio"
        disabled={disabled}
        className={`peer absolute inset-0 m-0 h-[22px] w-[22px] cursor-[inherit] opacity-0 ${className}`}
        {...rest}
      />
      <span
        aria-hidden
        className="pointer-events-none h-[22px] w-[22px] rounded-full border border-border-control bg-elevated transition-colors duration-fast peer-checked:border-accent peer-focus-visible:shadow-focus peer-disabled:opacity-45"
      />
      <span
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-1/2 h-[11px] w-[11px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent opacity-0 peer-checked:opacity-100"
      />
    </span>
  );
  if (label == null && description == null) return circle;
  return (
    <label
      className={`flex min-h-touch gap-3 ${description ? "items-start" : "items-center"} ${
        disabled ? "cursor-not-allowed" : "cursor-pointer"
      }`}
    >
      <span className={description ? "mt-0.5" : ""}>{circle}</span>
      {/* opacity here is the disabled-control exception, not text dimming */}
      <span className={`min-w-0 ${disabled ? "opacity-45" : ""}`}>
        {label != null && <span className="text-sm font-medium text-fg-default">{label}</span>}
        {description != null && (
          <span className="mt-0.5 block text-caption text-fg-muted">{description}</span>
        )}
      </span>
    </label>
  );
});
